import { useQuery, useMutation, useQueryClient, type UseMutationOptions, type UseQueryResult } from "@tanstack/react-query";
import {
  portfolioCurrentQueryOptions,
  portfolioHistoryMetricQueryOptions,
  alertPoliciesQueryOptions,
  createAlertPolicy,
  updateAlertPolicy,
  deleteAlertPolicy,
  alertEventsQueryOptions,
  ackAlertEvent,
  unackAlertEvent,
} from "@/services/queries/riskQueries";
import { riskQueryKeys } from "@/constants/queryKeys";
import type { AlertPolicyCreateRequest, AlertEventsResponse, AlertEvent } from "@/types/risk";

// Portfolio metrics
export const usePortfolioCurrent = (authToken?: string) => {
  return useQuery(portfolioCurrentQueryOptions(authToken));
};

export const usePortfolioHistoryMetric = (metricKey: string, authToken?: string) => {
  return useQuery(portfolioHistoryMetricQueryOptions(metricKey, authToken));
};

// Policies
export const useAlertPolicies = (authToken?: string) => {
  return useQuery(alertPoliciesQueryOptions(authToken));
};

export const useCreateAlertPolicy = (authToken?: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: { body: AlertPolicyCreateRequest }) => {
      return createAlertPolicy(authToken ? { body: params.body, authToken } : { body: params.body });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: riskQueryKeys.policies() });
      queryClient.invalidateQueries({ queryKey: riskQueryKeys.events() });
    },
  });
};

export const useUpdateAlertPolicy = (authToken?: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: { policyId: string; body: Partial<AlertPolicyCreateRequest> & { enabled?: boolean | null } }) => {
      return updateAlertPolicy(authToken ? { policyId: params.policyId, body: params.body, authToken } : { policyId: params.policyId, body: params.body });
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: riskQueryKeys.policies() });
      queryClient.invalidateQueries({ queryKey: riskQueryKeys.policy(variables.policyId) });
      queryClient.invalidateQueries({ queryKey: riskQueryKeys.events() });
    },
  });
};

export const useDeleteAlertPolicy = (authToken?: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: { policyId: string }) => {
      return deleteAlertPolicy(authToken ? { policyId: params.policyId, authToken } : { policyId: params.policyId });
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: riskQueryKeys.policies() });
      queryClient.invalidateQueries({ queryKey: riskQueryKeys.policy(variables.policyId) });
      queryClient.invalidateQueries({ queryKey: riskQueryKeys.events() });
    },
  });
};

// Events
export const useAlertEvents = (
  authToken?: string,
  limit: number = 100
): UseQueryResult<AlertEventsResponse, Error> => {
  const params = authToken ? { authToken, limit } : { limit };
  return useQuery(alertEventsQueryOptions(params));
};

type AckVars = { eventId: string };
type AckCtx = { prev?: AlertEventsResponse };

export const useAckAlertEvent = (authToken?: string) => {
  const queryClient = useQueryClient();
  return useMutation<AlertEvent, Error, AckVars, AckCtx>({
    mutationFn: async ({ eventId }) => ackAlertEvent(authToken ? { eventId, authToken } : { eventId }),
    onMutate: async (variables) => {
      await queryClient.cancelQueries({ queryKey: riskQueryKeys.events() });
      const prev = queryClient.getQueryData<AlertEventsResponse>(riskQueryKeys.events());
      queryClient.setQueryData<AlertEventsResponse>(riskQueryKeys.events(), (old) => {
        if (!old || !old.data) return old;
        const next: AlertEventsResponse = { ...old, data: [...old.data] };
        const idx = next.data.findIndex((e) => String(e.id) === String(variables.eventId));
        if (idx >= 0) {
          next.data[idx] = { ...(next.data[idx] as AlertEvent), status: 'ack', acked_at: new Date().toISOString() };
        }
        return next;
      });
      return { prev };
    },
    onError: (_err, _vars, context) => {
      if (context?.prev) {
        queryClient.setQueryData<AlertEventsResponse>(riskQueryKeys.events(), context.prev);
      }
    },
    onSuccess: (updated) => {
      queryClient.setQueryData<AlertEventsResponse>(riskQueryKeys.events(), (old) => {
        if (!old || !old.data) return old;
        const next: AlertEventsResponse = { ...old, data: [...old.data] };
        const idx = next.data.findIndex((e) => String(e.id) === String(updated.id));
        if (idx >= 0) next.data[idx] = updated;
        return next;
      });
    },
  } as UseMutationOptions<AlertEvent, Error, AckVars, AckCtx>);
};

type UnackVars = { eventId: string };
type UnackCtx = { prev?: AlertEventsResponse };

export const useUnackAlertEvent = (authToken?: string) => {
  const queryClient = useQueryClient();
  return useMutation<AlertEvent, Error, UnackVars, UnackCtx>({
    mutationFn: async ({ eventId }) => unackAlertEvent(authToken ? { eventId, authToken } : { eventId }),
    onMutate: async (variables) => {
      await queryClient.cancelQueries({ queryKey: riskQueryKeys.events() });
      const prev = queryClient.getQueryData<AlertEventsResponse>(riskQueryKeys.events());
      queryClient.setQueryData<AlertEventsResponse>(riskQueryKeys.events(), (old) => {
        if (!old || !old.data) return old;
        const next: AlertEventsResponse = { ...old, data: [...old.data] };
        const idx = next.data.findIndex((e) => String(e.id) === String(variables.eventId));
        if (idx >= 0) {
          next.data[idx] = { ...(next.data[idx] as AlertEvent), status: 'open', acked_at: null };
        }
        return next;
      });
      return { prev };
    },
    onError: (_err, _vars, context) => {
      if (context?.prev) {
        queryClient.setQueryData<AlertEventsResponse>(riskQueryKeys.events(), context.prev);
      }
    },
    onSuccess: (updated) => {
      queryClient.setQueryData<AlertEventsResponse>(riskQueryKeys.events(), (old) => {
        if (!old || !old.data) return old;
        const next: AlertEventsResponse = { ...old, data: [...old.data] };
        const idx = next.data.findIndex((e) => String(e.id) === String(updated.id));
        if (idx >= 0) next.data[idx] = updated;
        return next;
      });
    },
  } as UseMutationOptions<AlertEvent, Error, UnackVars, UnackCtx>);
};

